import type { Quiz } from '../types'

const tiles = (labels: string[], correct: number) =>
  labels.map((label, i) => (i === correct ? { label, correct: true } : { label }))
const judge = (prompt: string, truth: boolean, explanation: string): Quiz => ({
  format: 'judge',
  prompt,
  choices: truth
    ? [{ label: 'True', correct: true }, { label: 'False' }]
    : [{ label: 'True' }, { label: 'False', correct: true }],
  explanation,
})
const pick = (prompt: string, labels: string[], correct: number, explanation: string): Quiz => ({
  format: 'quick_pick',
  prompt,
  choices: tiles(labels, correct),
  explanation,
})
const spot = (code: string[], labels: string[], correct: number, explanation: string): Quiz => ({
  format: 'spot',
  prompt: 'Which pattern is this?',
  code: code.join('\n'),
  choices: tiles(labels, correct),
  explanation,
})

export const MORE_C: Record<string, Quiz[]> = {
  mediator: [
    judge(
      'In a Mediator, each component holds a reference to the hub, not to its peers.',
      true,
      'The hub is the only thing they know, so peers can change or be swapped freely.',
    ),
    pick(
      'Twelve form fields enable and disable each other. You route it all through one object. Which pattern?',
      ['Observer', 'Mediator', 'Facade', 'Command'],
      1,
      'One coordinator that peers talk through, so none of them knows the others.',
    ),
    pick(
      'Odd one out: which does NOT sit between parts to coordinate them?',
      ['Mediator', 'Facade', 'Memento', 'Event bus'],
      2,
      'Memento saves state; the other three stand between parts.',
    ),
    spot(
      [
        'class Room {',
        '  private array $users = [];',
        '  function join($u) {',
        '    $this->users[] = $u;',
        '  }',
        '  function say($from, $m) {',
        '    foreach ($this->users as $u) {',
        '      if ($u !== $from) $u->hear($m);',
        '    }',
        '  }',
        '}',
      ],
      ['Mediator', 'Strategy', 'None of these', 'Misused'],
      0,
      'Users talk only to the room, which relays; none holds another user.',
    ),
    spot(
      [
        'func (b *Button) Click() {',
        '\tb.form.name.Disable()',
        '\tb.form.email.Hide()',
        '\tb.dialog.Close()',
        '\tb.list.Reload()',
        '}',
        '',
        '// every widget names the others',
        '// directly, and no hub exists',
      ],
      ['Mediator', 'Observer', 'None of these', 'Misused'],
      2,
      'Widgets call each other directly; there is no hub, so no Mediator.',
    ),
  ],
  memento: [
    judge(
      'A Memento should let the caretaker open it and edit the saved fields.',
      false,
      'The caretaker only keeps it; only the originator reads and restores it.',
    ),
    pick(
      "You want undo without exposing an editor's private fields. Which pattern?",
      ['Command', 'Prototype', 'Memento', 'State'],
      2,
      'Memento stores a snapshot the originator alone can read back.',
    ),
    pick(
      'Odd one out: which does NOT help you step back to an earlier moment?',
      ['Memento', 'Command history', 'Event log replay', 'Singleton'],
      3,
      'Singleton limits instances; the others rebuild an earlier state.',
    ),
    spot(
      [
        'type Editor struct{ text string }',
        'type snap struct{ text string }',
        '',
        'func (e *Editor) Save() snap {',
        '\treturn snap{e.text}',
        '}',
        'func (e *Editor) Restore(s snap) {',
        '\te.text = s.text',
        '}',
      ],
      ['Memento', 'Prototype', 'None of these', 'Misused'],
      0,
      'The snapshot type is unexported: only Editor can make or read it.',
    ),
    spot(
      [
        'class Doc {',
        '  public array $lines = [];',
        '  function save(): array {',
        '    return $this->lines;',
        '  }',
        '}',
        '',
        '$doc->lines[] = "x";',
        '$old = $doc->save();',
        '$old[0] = "hacked";',
      ],
      ['Memento', 'Command', 'None of these', 'Misused'],
      3,
      'The saved array is open for anyone to edit, so the snapshot is not sealed.',
    ),
  ],
  observer: [
    judge(
      'In an Observer, the subject must know the concrete classes of its subscribers.',
      false,
      'It only knows an interface, so subscribers can come and go.',
    ),
    pick(
      'A price changes and any number of screens must update, none named by the price. Which pattern?',
      ['Mediator', 'Observer', 'Proxy', 'Iterator'],
      1,
      'A subject tells whoever subscribed, without knowing who they are.',
    ),
    pick(
      'Which problem does Observer solve?',
      [
        'Many parts react to one change',
        'Build objects step by step',
        'Undo the last action',
        'Add features by wrapping',
      ],
      0,
      'It fans a change out to subscribers without hard-wiring them.',
    ),
    spot(
      [
        'type Feed struct{ subs []func(Post) }',
        '',
        'func (f *Feed) On(fn func(Post)) {',
        '\tf.subs = append(f.subs, fn)',
        '}',
        'func (f *Feed) Publish(p Post) {',
        '\tfor _, fn := range f.subs {',
        '\t\tfn(p)',
        '\t}',
        '}',
      ],
      ['Observer', 'Mediator', 'None of these', 'Misused'],
      0,
      'Callers subscribe; Publish notifies all without knowing them.',
    ),
    spot(
      [
        'class Cart {',
        '  function add($item) {',
        '    $this->items[] = $item;',
        '    (new Mailer)->promo($item);',
        '    (new Stock)->drop($item);',
        '    (new Stats)->hit($item);',
        '  }',
        '}',
      ],
      ['Observer', 'Strategy', 'None of these', 'Misused'],
      2,
      'The cart names each reaction itself; nothing can subscribe.',
    ),
  ],
  state: [
    judge(
      'In State, the object swaps behaviour by changing which state object it holds.',
      true,
      'Calls are forwarded to the current state, so behaviour follows it.',
    ),
    pick(
      'A ticket acts differently as Open, Paid or Closed, and each moves to the next. Which pattern?',
      ['Strategy', 'Chain of Resp.', 'State', 'Memento'],
      2,
      'Behaviour depends on the current state, which also picks the next.',
    ),
    pick(
      'Odd one out: three share a property. Which differs?',
      ['State', 'Strategy', 'Bridge', 'Singleton'],
      3,
      'The first three delegate work to a swappable object; Singleton does not.',
    ),
    spot(
      [
        'type Door struct{ s doorState }',
        'type doorState interface{ Push(*Door) }',
        '',
        'type locked struct{}',
        'func (locked) Push(d *Door) {}',
        '',
        'type shut struct{}',
        'func (shut) Push(d *Door) {',
        '\td.s = open{}',
        '}',
      ],
      ['State', 'Strategy', 'None of these', 'Misused'],
      0,
      'Each state decides what Push does and which state follows.',
    ),
    spot(
      [
        'class Order {',
        '  function ship() {',
        '    if ($this->status === "new") {',
        '      throw new Exception("pay first");',
        '    } elseif ($this->status === "paid") {',
        '      $this->status = "shipped";',
        '    }',
        '  }',
        '}',
      ],
      ['State', 'Strategy', 'None of these', 'Misused'],
      2,
      'A plain status string with if branches; no state objects at all.',
    ),
  ],
  strategy: [
    judge(
      'Strategy lets a caller pick among interchangeable algorithms at run time.',
      true,
      'Each algorithm sits behind one interface, so the choice is swappable.',
    ),
    pick(
      'Shipping cost is computed one of five ways and you pick per order. Which pattern?',
      ['Strategy', 'State', 'Builder', 'Adapter'],
      0,
      'Each way is an interchangeable algorithm behind one interface.',
    ),
    pick(
      'Odd one out: which does NOT swap one way of doing a job for another?',
      ['Strategy', 'Sort comparator', 'Payment driver', 'Memento'],
      3,
      'Memento snapshots state; the other three swap the algorithm used.',
    ),
    spot(
      [
        'type Rule func(total float64) float64',
        '',
        'func Price(t float64, r Rule) float64 {',
        '\treturn r(t)',
        '}',
        '',
        'ten := func(t float64) float64 {',
        '\treturn t * 0.9',
        '}',
        'Price(100, ten)',
      ],
      ['Strategy', 'Observer', 'None of these', 'Misused'],
      0,
      'The algorithm is passed in as a function and called by the context.',
    ),
    spot(
      [
        'interface Tax { function of($n); }',
        '',
        'class Cart {',
        '  function total($n, Tax $t) {',
        '    if ($t instanceof UkTax) {',
        '      return $n * 1.2;',
        '    }',
        '    return $t->of($n);',
        '  }',
        '}',
      ],
      ['Strategy', 'State', 'None of these', 'Misused'],
      3,
      'It type-checks one strategy, which defeats the interface.',
    ),
  ],
  'template-method': [
    judge(
      'In Template Method the base class fixes the order of steps and subclasses fill in some.',
      true,
      'The skeleton stays in the base; only chosen steps are overridden.',
    ),
    pick(
      'Three importers share the same read, check, save flow but parse differently. Which pattern?',
      ['Strategy', 'Template Method', 'Factory Method', 'Visitor'],
      1,
      'One fixed skeleton in a base class, with subclasses overriding steps.',
    ),
    pick(
      'Which problem does Template Method solve?',
      [
        'Same steps repeated in variants',
        'Many objects need one change',
        'Two parts depend on each other',
        'Slow object construction',
      ],
      0,
      'The shared order lives once in the base; only differences vary.',
    ),
    spot(
      [
        'abstract class Report {',
        '  final function run() {',
        '    $d = $this->load();',
        '    $r = $this->render($d);',
        '    $this->send($r);',
        '  }',
        '  abstract function load();',
        '  abstract function render($d);',
        '}',
      ],
      ['Template Method', 'Strategy', 'None of these', 'Misused'],
      0,
      'run() fixes the sequence; subclasses supply load and render.',
    ),
    spot(
      [
        'type Job struct{ steps []func() }',
        '',
        'func (j *Job) Run() {',
        '\tfor _, s := range j.steps {',
        '\t\ts()',
        '\t}',
        '}',
        '',
        '// steps are chosen by the caller',
      ],
      ['Template Method', 'Observer', 'None of these', 'Misused'],
      2,
      'There is no base class with a fixed skeleton, only a list of funcs.',
    ),
  ],
  visitor: [
    judge(
      'Visitor is a good fit when new node types are added more often than new operations.',
      false,
      'It is the reverse: a new node type means editing every visitor.',
    ),
    pick(
      'You need a new export over a stable tree of node types, without editing them. Which pattern?',
      ['Iterator', 'Composite', 'Visitor', 'Decorator'],
      2,
      'Visitor adds an operation from outside, node classes untouched.',
    ),
    pick(
      'Odd one out: which does NOT walk or process a structure of parts?',
      ['Visitor', 'Iterator', 'Composite', 'Flyweight'],
      3,
      'Flyweight shares data to save memory; the others work on structures.',
    ),
    spot(
      [
        'interface Shape { function accept(V $v); }',
        '',
        'class Circle implements Shape {',
        '  function accept(V $v) {',
        '    return $v->circle($this);',
        '  }',
        '}',
        'class Area implements V {',
        '  function circle($c) {',
        '    return 3.14 * $c->r ** 2;',
        '  }',
        '}',
      ],
      ['Visitor', 'Strategy', 'None of these', 'Misused'],
      0,
      'accept() calls back the visitor method for its own type.',
    ),
    spot(
      [
        'func Total(n Node) int {',
        '\tswitch v := n.(type) {',
        '\tcase Leaf:',
        '\t\treturn v.N',
        '\tcase Pair:',
        '\t\treturn Total(v.L) + Total(v.R)',
        '\t}',
        '\treturn 0',
        '}',
      ],
      ['Visitor', 'Composite', 'None of these', 'Misused'],
      2,
      'A plain type switch; no accept/visit double dispatch is used.',
    ),
  ],
}
